import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Toast from '../common/Toast';

const PROVIDERS = [
    {
        param: 'google',
        connected: 'Gmail connected. New deals will import history automatically.',
        failed: 'Gmail connection failed',
    },
    {
        param: 'granola',
        connected: 'Granola connected. You can pull meeting summaries into CRM.',
        failed: 'Granola connection failed',
    },
] as const;

/**
 * Gmail and Granola OAuth land on the CRM page with `?google=` / `?granola=` `connected|error`.
 * Show the result once and drop the params from the URL. (Google Drive: `GoogleDriveConnectNotice`.)
 */
const CrmIntegrationConnectNotice: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [notice, setNotice] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const provider = PROVIDERS.find((p) => params.has(p.param));
        if (!provider) return;
        const result = params.get(provider.param);
        const reason = params.get('reason');

        if (result === 'connected') {
            setNotice({ type: 'success', message: provider.connected });
        } else {
            setNotice({ type: 'error', message: `${provider.failed}${reason ? `: ${reason}` : ''}` });
        }

        params.delete(provider.param);
        params.delete('reason');
        const qs = params.toString();
        navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- react to OAuth landing params only
    }, [location.search]);

    if (!notice) return null;
    // Errors stay until dismissed; success auto-hides
    return (
        <Toast
            message={notice.message}
            type={notice.type}
            persistent={notice.type === 'error'}
            onClose={() => setNotice(null)}
        />
    );
};

export default CrmIntegrationConnectNotice;
